/**
 * Map API / axios errors to plain-language messages for end users.
 * Never show HTTP status codes, internal slugs, or stack traces in the UI.
 */

const AUTH_MESSAGE_MAP: Record<string, string> = {
  "invalid credentials":
    "The email or password you entered is incorrect. Please try again.",
  "account temporarily locked, please try again later":
    "Your account is temporarily locked after too many failed sign-in attempts. Please try again in about 15 minutes.",
  "too many failed attempts - please try again later":
    "Too many sign-in attempts. Please wait a few minutes and try again.",
  "account locked":
    "Your account is temporarily locked. Please try again later or contact your administrator.",
  "account inactive":
    "This account is not active. Please contact your administrator.",
  "invalid or expired mfa token":
    "Your verification session expired. Please sign in again.",
  "invalid code":
    "That verification code is incorrect. Please check the code and try again.",
  "invalid or expired setup token":
    "Your setup session expired. Please sign in again to continue.",
  "mfa not configured on this account":
    "Multi-factor authentication is required but not set up on this account. Contact your administrator.",
  "could not send verification email":
    "We couldn't send a verification email right now. Please try again in a few minutes.",
  "user not provisioned":
    "Your Microsoft account is not set up for this portal. Contact your administrator.",
  "organization is not active":
    "Your organization is not active. Please contact your administrator.",
  "refresh token required":
    "Your session has expired. Please sign in again.",
  "invalid refresh token":
    "Your session has expired. Please sign in again.",
  "refresh token not found or revoked":
    "Your session has expired. Please sign in again.",
  "refresh token expired or revoked":
    "Your session has expired. Please sign in again.",
  "internal server error":
    "Something went wrong on our end. Please try again in a few minutes.",
};

function normalizeKey(value: string): string {
  return value
    .replace(/^\[\d{3}\]\s*/g, "")
    .replace(/:\s*.+$/, "")
    .trim()
    .toLowerCase();
}

function isAxiosStatusMessage(value: string): boolean {
  return /^request failed with status code \d{3}$/i.test(value.trim());
}

function isMostlyTechnical(value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  if (isAxiosStatusMessage(v)) return true;
  if (/^\[\d{3}\]/.test(v)) return true;
  if (/^(unauthorized|forbidden|bad request|not found|conflict|resource not found|internal server error)$/i.test(v)) return true;
  if (/^HTTP \d{3}/i.test(v)) return true;
  return false;
}

/* ── Backend / runtime error translation ─────────────────────────────── */

const SERVER_PROBLEM = "Something went wrong on our end. Please try again in a few minutes.";
const NETWORK_PROBLEM = "We couldn't reach the server. Check your internet connection and try again.";
const TIMEOUT_PROBLEM = "This is taking longer than expected. Please try again.";
const STALE_PAGE = "Something on this page is out of date. Refresh the page and try again.";

/** Strings that only make sense to a developer (DB, Go runtime, JSON, transport). */
const TECHNICAL_MARKERS =
  /\b(pq|sql|sqlstate|gorm|pgx|postgres|goroutine|panic|nil pointer|runtime error|invalid memory|unmarshal|go struct|go value|strconv|dial tcp|rpc error|stack trace|traceback|exception|syntax error at|null value in column|violates|constraint|relation "|column "|reflect|interface \{\}|0x[0-9a-f]{4,})\b|json:|\bat [A-Za-z]+\.[A-Za-z]+ \(|status code \d{3}/i;

/** "PatientID" / "date_of_birth" / "Items[0].Code" → "Patient" / "Date of birth" / "Code". */
function fieldLabel(path: string): string {
  const last = (path.split(".").pop() ?? path).replace(/\[\d+\]/g, "");
  const words = last
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b(I[Dd])\b$/, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => (/^[A-Z0-9]{2,}$/.test(w) ? w : i === 0 ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()));
  return words.join(" ") || "A field";
}

function validationPhrase(tag: string): string {
  switch (tag) {
    case "required":
    case "required_if":
    case "required_with":
    case "required_without":
      return "is required";
    case "email":
      return "must be a valid email address";
    case "e164":
      return "must be a valid phone number";
    case "min":
      return "is too short";
    case "max":
      return "is too long";
    case "oneof":
      return "has a value that isn't allowed";
    case "uuid":
    case "uuid4":
      return "is missing or invalid";
    case "datetime":
      return "must be a valid date";
    case "gt":
    case "gte":
    case "lt":
    case "lte":
      return "is out of range";
    default:
      return "isn't valid";
  }
}

/** "failed to create patient: ..." → "create patient" (null when not that shape). */
function failedAction(value: string): string | null {
  const m = /^(?:failed|unable|could not|couldn't|cannot)\s+(?:to\s+)?([a-z][a-z0-9 /'-]{2,60}?)\s*(?::|$)/i.exec(value);
  if (!m) return null;
  const action = m[1]!.trim();
  // Only translate verb phrases ("create patient"), not "find patient record" style sentences we'd mangle.
  return /^(create|update|save|delete|remove|load|fetch|get|list|send|submit|upload|download|generate|sign|cancel|add|insert|read|parse|process|start|open|verify|check|sync|schedule|book)\b/i.test(action)
    ? action.replace(/^(fetch|get|list|read|insert)\b/i, (v) => ({ fetch: "load", get: "load", list: "load", read: "load", insert: "save" })[v.toLowerCase()] ?? v)
    : null;
}

function sentence(value: string): string {
  const v = value.trim();
  if (!v) return v;
  const capped = v[0]!.toUpperCase() + v.slice(1);
  return /[.!?…)]$/.test(capped) ? capped : `${capped}.`;
}

/**
 * Translate a raw backend/runtime message into plain language.
 * Returns null when the message has no recognisable technical shape
 * (the caller then decides whether to show it or fall back).
 */
function translateTechnical(raw: string, status?: number): string | null {
  const v = raw.trim();
  const lower = v.toLowerCase();

  // Transport / browser
  if (/^(network error|failed to fetch|load failed|networkerror)/i.test(v) || /connection refused|no such host|econnrefused|err_connection/i.test(v)) {
    return NETWORK_PROBLEM;
  }
  if (/timeout of \d+ms exceeded|context deadline exceeded|i\/o timeout|request timed out/i.test(v)) return TIMEOUT_PROBLEM;
  if (/^(context canceled|request aborted|canceled)$/i.test(v)) return "The request was interrupted. Please try again.";

  // Go request validation: Key: 'Req.PatientID' Error:Field validation for 'PatientID' failed on the 'required' tag
  const fieldErrors = [...v.matchAll(/Key: '([^']+)' Error:Field validation for '[^']+' failed on the '([^']+)' tag/g)];
  if (fieldErrors.length > 0) {
    const parts = fieldErrors.slice(0, 3).map(([, path, tag]) => `${fieldLabel(path!)} ${validationPhrase(tag!)}`);
    return sentence(parts.join(". "));
  }
  const unmarshal = /cannot unmarshal \w+ into Go (?:struct field|value) ([\w.[\]]+)/i.exec(v);
  if (unmarshal) return sentence(`${fieldLabel(unmarshal[1]!)} isn't in the right format`);
  if (/parsing time|cannot parse .* as|invalid date|month out of range|day out of range/i.test(v)) {
    return "A date or time isn't in a valid format. Please check it and try again.";
  }
  if (/invalid uuid|invalid .*\bid\b|uuid: /i.test(v)) return STALE_PAGE;
  if (/unexpected end of json|invalid character|unknown field|^eof$|cannot bind|request body/i.test(v)) {
    return "Some of the information couldn't be read. Please review the form and try again.";
  }

  // Database
  if (/duplicate key|unique constraint|23505/i.test(v)) {
    const what = /email/i.test(v) ? "A record with this email" : /npi/i.test(v) ? "A record with this NPI" : /phone/i.test(v) ? "A record with this phone number" : "This record";
    return `${what} already exists.`;
  }
  if (/foreign key|23503|still referenced/i.test(v)) {
    return "This is linked to other records, so it can't be changed or removed right now.";
  }
  if (/not-null|null value in column|23502/i.test(v)) return "Some required information is missing. Please fill in all required fields.";
  if (/no rows in result set|record not found|resource not found/i.test(lower)) {
    return "We couldn't find that record. It may have been removed - refresh the page and try again.";
  }

  // "failed to create patient: pq: ..." → "Couldn't create patient. Please try again."
  const action = failedAction(v);
  if (action && (status === undefined || status >= 500 || TECHNICAL_MARKERS.test(v) || v.includes(":"))) {
    return `Couldn't ${action.toLowerCase()}. Please try again.`;
  }

  if (TECHNICAL_MARKERS.test(v)) return status && status < 500 ? fallbackGeneral(status) : SERVER_PROBLEM;
  return null;
}

function fallbackForAuth(status?: number): string {
  switch (status) {
    case 401:
      return "The email or password you entered is incorrect. Please try again.";
    case 403:
      return "You don't have access to sign in here. Contact your administrator if you need help.";
    case 429:
      return "Too many sign-in attempts. Please wait a few minutes and try again.";
    case 503:
      return "Sign-in is temporarily unavailable. Please try again in a few minutes.";
    default:
      return "We couldn't sign you in. Please check your details and try again.";
  }
}

function fallbackGeneral(status?: number): string {
  switch (status) {
    case 400:
      return "Something in your request wasn't valid. Please review and try again.";
    case 401:
      return "Your session expired. Please sign in again.";
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return "We couldn't find what you were looking for.";
    case 409:
      return "This action conflicts with existing data. Refresh the page and try again.";
    case 422:
      return "Some of the information provided couldn't be saved. Please review and try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 402:
      return "This feature isn't included in your organization's plan. An administrator can upgrade under Settings → Organization → Billing.";
    case 413:
      return "That file is too large. Please choose a smaller file.";
    case 502:
    case 504:
      return "The server took too long to respond. Please try again in a moment.";
    case 503:
      return "This service is temporarily unavailable. Please try again shortly.";
    default:
      return status && status >= 500 ? SERVER_PROBLEM : "Something went wrong. Please try again.";
  }
}

/** True when the URL is part of the sign-in flow (login, MFA, SSO). */
export function isAuthFlowUrl(url?: string): boolean {
  if (!url) return false;
  return /\/auth\/(login|mfa|sso)/.test(url); // includes /auth/login/select-org
}

/**
 * Convert a raw API message into user-friendly copy.
 * @param context - `"login"` for sign-in screens; `"general"` elsewhere.
 */
export function toUserMessage(
  raw: string | undefined,
  status?: number,
  url?: string,
  context: "login" | "general" = "general",
): string {
  const authContext = context === "login" || isAuthFlowUrl(url);

  if (!raw || isMostlyTechnical(raw)) {
    return authContext ? fallbackForAuth(status) : fallbackGeneral(status);
  }

  const key = normalizeKey(raw);
  if (AUTH_MESSAGE_MAP[key]) {
    return AUTH_MESSAGE_MAP[key];
  }

  // Partial matches for backend detail slugs (never show slugs verbatim).
  if (key.includes("invalid credentials") || key.includes("account inactive")) {
    return AUTH_MESSAGE_MAP["invalid credentials"]!;
  }
  if (key.includes("refresh token")) {
    return AUTH_MESSAGE_MAP["refresh token required"]!;
  }
  if (key.includes("locked")) {
    return AUTH_MESSAGE_MAP["account temporarily locked, please try again later"]!;
  }
  if (key.includes("mfa") && key.includes("expired")) {
    return AUTH_MESSAGE_MAP["invalid or expired mfa token"]!;
  }
  if (key.includes("too many")) {
    return AUTH_MESSAGE_MAP["too many failed attempts - please try again later"]!;
  }

  // If it still looks like a developer string (snake_case slug), use a safe fallback.
  if (/^[a-z0-9_ -]+$/.test(key) && key.includes("_")) {
    return authContext ? fallbackForAuth(status) : fallbackGeneral(status);
  }

  const translated = translateTechnical(raw, status);
  if (translated) return authContext && status !== undefined && status < 500 ? fallbackForAuth(status) : translated;

  // Server faults rarely carry wording meant for people - only keep short, plain sentences.
  if (status !== undefined && status >= 500 && (raw.length > 160 || /[{}<>=]|\w+\.\w+\(/.test(raw))) {
    return fallbackGeneral(status);
  }

  return sentence(raw);
}

/**
 * Last-line cleanup for any text about to appear in an error toast or banner,
 * including strings that never went through the API client (fetch, sockets,
 * thrown Errors). Returns "" when nothing useful remains.
 */
export function cleanErrorText(text: string | undefined | null): string {
  const v = (text ?? "").trim();
  if (!v || /^(undefined|null|\[object object\]|error|unknown error)$/i.test(v)) return "";
  if (isAxiosStatusMessage(v) || /^\[\d{3}\]/.test(v) || /^HTTP \d{3}/i.test(v)) {
    const status = Number(/(\d{3})/.exec(v)?.[1]);
    return fallbackGeneral(Number.isFinite(status) ? status : undefined);
  }
  if (/^(unauthorized|forbidden|bad request|not found|conflict|resource not found|internal server error)$/i.test(v)) {
    return fallbackGeneral();
  }
  return translateTechnical(v) ?? v;
}

/** "Failed to save allergy" → "Couldn't save allergy" for toast titles. */
export function cleanErrorTitle(title: string | undefined | null): string {
  const v = (title ?? "").trim();
  if (!v) return "";
  if (TECHNICAL_MARKERS.test(v) || isAxiosStatusMessage(v) || /^(network error|failed to fetch)/i.test(v)) {
    return cleanErrorText(v) || "Something went wrong";
  }
  return v.replace(/^failed to\s+/i, "Couldn't ");
}

/** Extract a friendly message from any thrown value (Error, axios, unknown). */
export function getErrorMessage(
  err: unknown,
  context: "login" | "general" = "general",
): string {
  let message = "";
  let status: number | undefined;
  let url: string | undefined;

  // Inspect if this is an Axios error containing API validation details
  const axiosErr = err as {
    response?: {
      status?: number;
      config?: { url?: string };
      data?: any;
    };
    message?: string;
  };

  if (axiosErr && axiosErr.response) {
    status = axiosErr.response.status;
    url = axiosErr.response.config?.url;
    
    const data = axiosErr.response.data;
    if (data) {
      if (typeof data === "string") {
        message = data;
      } else {
        // Try details or nested error.details first, then message properties
        message =
          data.details ||
          (typeof data.error === "string" ? data.error : undefined) ||
          data.error?.details ||
          data.message ||
          data.error?.message ||
          "";
      }
    }
  }

  if (!message && err instanceof Error) {
    message = err.message;
    const withMeta = err as any;
    if (typeof withMeta.status === "number") status = withMeta.status;
    if (typeof withMeta.url === "string") url = withMeta.url;
  }

  if (typeof err === "string") {
    message = err;
  }

  if (!message) {
    return context === "login" ? fallbackForAuth(status) : fallbackGeneral(status);
  }

  return toUserMessage(message, status, url, context);
}

/** Convenience for login / MFA forms. */
export function getLoginErrorMessage(err: unknown): string {
  return getErrorMessage(err, "login");
}
